import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

import DashboardLayout from '../components/DashboardLayout.vue'
import SignIn from '../views/auth/sign-in.vue'
import SignUp from '../views/auth/sign-up.vue'
import ForgotPassword from '../views/auth/forgot-password.vue'
import ResetPassword from '../views/auth/reset-password.vue'
import DashboardList from '../views/dashboard/dashboard.vue'
import Analytics from '../views/analytics/analytics.vue'
import AnalyticsOverview from '../views/analytics/overview.vue'
import AnalyticsLives from '../views/analytics/lives.vue'
import AnalyticsMedias from '../views/analytics/medias.vue'
import AnalyticsAds from '../views/analytics/ads.vue'
import Lives from '../views/lives/lives.vue'
import LivesList from '../views/lives/list.vue'
import LivesEdit from '../views/lives/edit.vue'
import Medias from '../views/medias/medias.vue'
import MediasList from '../views/medias/list.vue'
import MediaEdit from '../views/medias/edit.vue'
import Slates from '../views/slates/slates.vue'
import SlatesList from '../views/slates/list.vue'
import SlateEdit from '../views/slates/edit.vue'
import Recording from '../views/recording/recording.vue'
import RecordingList from '../views/recording/list.vue'
import Tags from '../views/tags/tags.vue'
import TagsList from '../views/tags/list.vue'
import Attributes from '../views/attributes/attributes.vue'
import AttributesList from '../views/attributes/list.vue'
import Webhooks from '../views/webhooks/webhooks.vue'
import WebhooksList from '../views/webhooks/list.vue'
import WebhooksHistory from '../views/webhooks/history.vue'
import ApiKeys from '../views/api-keys/api-keys.vue'
import ApiKeysList from '../views/api-keys/list.vue'
import Categories from '../views/categories/categories.vue'
import CategoriesList from '../views/categories/list.vue'
import ProfileView from '../views/profile/profile.vue'
import DeveloperDocs from '../views/developer/developer.vue'
import Users from '../views/users/users.vue'
import UsersList from '../views/users/list.vue'
import UsersInvites from '../views/users/invites.vue'
import UserEdit from '../views/users/edit.vue'

import PublishingView from '../views/publishing/publishing.vue'
import Editor from '../views/editor/editor.vue'

const routes = [
  {
    path: '/login',
    name: 'sign-in',
    component: SignIn,
    meta: { guest: true }
  },
  {
    path: '/sign-up/:token',
    name: 'sign-up',
    component: SignUp,
    meta: { guest: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
    meta: { guest: true }
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: ResetPassword,
    meta: { guest: true }
  },
  {
    path: '/editor',
    name: 'editor',
    component: Editor,
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/dashboard'
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: DashboardList,
        meta: { title: 'Dashboard', description: 'Overview of your livestreaming platform' }
      },
      {
        path: 'analytics',
        component: Analytics,
        meta: { title: 'Analytics', description: 'Stream performance and viewer insights' },
        children: [
          {
            path: '',
            redirect: '/analytics/overview'
          },
          {
            path: 'overview',
            name: 'analytics-overview',
            component: AnalyticsOverview,
            meta: { title: 'Analytics Overview', description: 'Cross-stream summary' }
          },
          {
            path: 'lives',
            name: 'analytics-lives',
            component: AnalyticsLives,
            meta: { title: 'Lives Analytics', description: 'Per-live performance and viewer insights' }
          },
          {
            path: 'medias',
            name: 'analytics-medias',
            component: AnalyticsMedias,
            meta: { title: 'Medias Analytics', description: 'Per-media performance and listener insights' }
          },
          {
            path: 'ads',
            name: 'analytics-ads',
            component: AnalyticsAds,
            meta: { title: 'Ads Analytics', description: 'Ad break performance: how long listeners spend in ads' }
          }
        ]
      },
      {
        path: 'lives',
        component: Lives,
        meta: { title: 'Lives', description: 'Manage your livestream channels', permission: 'lives' },
        children: [
          {
            path: '',
            name: 'lives',
            component: LivesList
          },
          {
            path: ':id/edit',
            name: 'lives-edit',
            component: LivesEdit,
            meta: { title: 'Edit Live', description: 'Configure livestream settings' }
          }
        ]
      },
      {
        path: 'medias',
        component: Medias,
        meta: { title: 'Medias', description: 'Manage your media library', permission: 'medias' },
        children: [
          {
            path: '',
            name: 'medias',
            component: MediasList
          },
          {
            path: ':id/edit',
            name: 'medias-edit',
            component: MediaEdit,
            meta: { title: 'Edit Media', description: 'Update media details and manage formats' }
          }
        ]
      },
      {
        path: 'slates',
        component: Slates,
        meta: { title: 'Slates', description: 'Manage your slate video library', permission: 'slates' },
        children: [
          {
            path: '',
            name: 'slates',
            component: SlatesList
          },
          {
            path: ':id/edit',
            name: 'slates-edit',
            component: SlateEdit,
            meta: { title: 'Edit Slate', description: 'Update slate details' }
          }
        ]
      },
      {
        path: 'recording',
        component: Recording,
        meta: { title: 'Recording', description: 'Manage stream recordings', permission: 'lives' },
        children: [
          {
            path: '',
            name: 'recording',
            component: RecordingList
          }
        ]
      },
      {
        path: 'tags',
        component: Tags,
        meta: { title: 'Tags', description: 'Organize content with tags', permission: 'tags' },
        children: [
          {
            path: '',
            name: 'tags',
            component: TagsList
          }
        ]
      },
      {
        path: 'custom-attributes',
        component: Attributes,
        meta: { title: 'Custom Attributes', description: 'Define custom fields for resources', permission: 'attributes' },
        children: [
          {
            path: '',
            name: 'attributes',
            component: AttributesList
          }
        ]
      },
      {
        path: 'webhooks',
        component: Webhooks,
        meta: { title: 'Webhooks', description: 'Configure event notifications', permission: 'webhooks' },
        children: [
          {
            path: '',
            name: 'webhooks',
            component: WebhooksList
          },
          {
            path: ':id/history',
            name: 'webhooks-history',
            component: WebhooksHistory
          }
        ]
      },
      {
        path: 'api-keys',
        component: ApiKeys,
        meta: { title: 'API Keys', description: 'Manage API access keys', permission: 'api_keys' },
        children: [
          {
            path: '',
            name: 'api-keys',
            component: ApiKeysList
          }
        ]
      },
      {
        path: 'categories',
        component: Categories,
        meta: { title: 'Categories', description: 'Organize content with hierarchical categories', permission: 'categories' },
        children: [
          {
            path: '',
            name: 'categories',
            component: CategoriesList
          }
        ]
      },
      {
        path: 'publishing',
        name: 'publishing',
        component: PublishingView,
        meta: { title: 'Publishing', description: 'Manage social media publishing channels' }
      },
      {
        path: 'profile',
        name: 'profile',
        component: ProfileView,
        meta: { title: 'Profile', description: 'Manage your personal information and security' }
      },
      {
        path: 'developer',
        name: 'developer',
        component: DeveloperDocs,
        meta: { title: 'Developer', description: 'SDKs and API references for integrating with the platform' }
      },
      {
        path: 'users',
        component: Users,
        meta: { title: 'Users', description: 'Manage team members and access', requiresAdmin: true },
        children: [
          {
            path: '',
            name: 'users',
            component: UsersList
          },
          {
            path: 'invites',
            name: 'users-invites',
            component: UsersInvites,
            meta: { title: 'Invites', description: 'Manage team invitations' }
          },
          {
            path: ':id/edit',
            name: 'users-edit',
            component: UserEdit,
            meta: { title: 'Edit User', description: 'Update user information and permissions' }
          }
        ]
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0, left: 0 }
  }
})

let authChecked = false

router.beforeEach(async (to, from, next) => {
  const token = store.getters['auth/token']

  // On first load, verify token with server
  if (token && !authChecked) {
    try {
      await store.dispatch('auth/fetchUser')
    } catch (error) {
      // Only logout if server responded (e.g. 401), not on network failure
      if (error?.status_code === 401 || error?.response?.status === 401) {
        store.dispatch('auth/logout')
      }
    }
    authChecked = true
  }

  const isAuthenticated = store.getters['auth/isAuthenticated']
  const requiresAuth = to.matched.some(r => r.meta.requiresAuth)
  const isGuest = to.meta.guest

  if (requiresAuth && !isAuthenticated) {
    next('/login')
  } else if (isGuest && isAuthenticated) {
    next('/dashboard')
  } else if (to.matched.some(r => r.meta.requiresAdmin)) {
    const isAdmin = store.getters['auth/isAdmin']
    if (!isAdmin) {
      next('/dashboard')
    } else {
      next()
    }
  } else if (to.meta.permission) {
    const hasPermission = store.getters['auth/hasPermission'](to.meta.permission)
    if (!hasPermission) {
      next('/dashboard')
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
